import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Icons } from './constants/icons.constants';
import { IconService } from './service/icon.service';

/**
 * How to include the icons within the project
 *
 * Add those files into your angular.json file
 * @example
 * "assets": [
 *    {
 *      "glob": "**\/*",
 *      "input": "./node_modules/@netwealth/nw-components/assets",
 *      "output": "/assets/"
 *    }
 *  ],
 */
@Component({
    selector: 'nw-icon',
    template: '<ng-content></ng-content>',
    styleUrls: ['./icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [IconService]
})
export class IconComponent implements OnChanges {
    @Input() public icon!: Icons.Type | Icons.ThirdParty;
    @Input() public preserveAspectRatio?: Icons.PreserveAspectRatio;

    private _currentIconFetch = Subscription.EMPTY;
    private iconService: IconService = inject(IconService);
    private elementRef: ElementRef = inject(ElementRef);

    public ngOnChanges({ icon, preserveAspectRatio }: SimpleChanges): void {
        if (icon?.currentValue || preserveAspectRatio?.currentValue) {
            this._currentIconFetch.unsubscribe();

            this._currentIconFetch = this.iconService
                .getIcon(this.icon, this.preserveAspectRatio)
                .pipe(take(1))
                .subscribe(
                    (svg) => this._setSvgElement(svg),
                    (err: Error) => {
                        throw new Error(
                            `Error retrieving icon ${this.icon}! ${err.message}`
                        );
                    }
                );
        }
    }

    private _setSvgElement(svg: SVGElement) {
        this._clearSvgElement();

        this.elementRef.nativeElement.appendChild(svg);
    }

    private _clearSvgElement() {
        const layoutElement: HTMLElement = this.elementRef.nativeElement;
        let childCount = layoutElement.childNodes.length;

        // Remove existing non-element child nodes and SVGs, and add the new SVG element. Note that
        // we can't use innerHTML, because IE will throw if the element has a data binding.
        while (childCount--) {
            const child = layoutElement.childNodes[childCount];

            // 1 corresponds to Node.ELEMENT_NODE. We remove all non-element nodes in order to get rid
            // of any loose text nodes, as well as any SVG elements in order to remove any old icons.
            if (child.nodeType !== 1 || child.nodeName.toLowerCase() === 'svg') {
                child.remove();
            }
        }
    }
}
